import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /**
   * Safe getter for the dialog element reference
   * @returns {HTMLDialogElement | null}
   */
  get dialogElement() {
    const dialogRef = this.refs?.dialog;
    if (dialogRef instanceof HTMLDialogElement) return dialogRef;
    if (Array.isArray(dialogRef) && dialogRef[0] instanceof HTMLDialogElement) return dialogRef[0];
    const queried = this.querySelector('dialog');
    return queried instanceof HTMLDialogElement ? queried : null;
  }

  /** @type {boolean} Tracks whether this element has been connected at least once. */
  #hasConnected = false;

  connectedCallback() {
    super.connectedCallback();

    const dialog = this.dialogElement;
    // Only close a server-rendered open dialog on the very first connection.
    // If the element is disconnected and reconnected by a DOM morph while the
    // shopper has the drawer open (e.g. after deleting a cart item), we must
    // not force-close it — that causes the visible "close then reopen" jump.
    if (!this.#hasConnected && dialog && !window.Shopify?.designMode) {
      if (dialog.open) {
        dialog.close();
      }
      if (document.activeElement && dialog.contains(document.activeElement)) {
        document.activeElement.blur();
      }
    }
    this.#hasConnected = true;

    if (dialog) {
      dialog.addEventListener('close', this.#stopVideos);
    }

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const dialog = this.dialogElement;
    if (dialog) {
      dialog.removeEventListener('close', this.#stopVideos);
    }
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('touchmove', this.#handleTouchMove);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  #loadAndPlayVideos() {
    const dialog = this.dialogElement;
    if (!dialog) return;

    const iframe = dialog.querySelector('iframe[data-src]');
    if (iframe) {
      iframe.src = iframe.dataset.src;
    }

    const video = dialog.querySelector('video');
    if (video) {
      if (video.dataset.src) {
        video.src = video.dataset.src;
      }
      video.play().catch(() => {});
    }
  }

  #stopVideos = () => {
    const dialog = this.dialogElement;
    if (!dialog) return;

    const iframe = dialog.querySelector('iframe');
    if (iframe) {
      if (!iframe.dataset.src && iframe.src) {
        iframe.dataset.src = iframe.src;
      }
      iframe.src = '';
    }

    const video = dialog.querySelector('video');
    if (video) {
      video.pause();
      video.currentTime = 0;
      if (video.dataset.src) {
        video.src = '';
      }
    }
  };

  /**
   * Shows the dialog.
   */
  showDialog() {
    const dialog = this.dialogElement;

    if (!dialog || dialog.open) return;

    this.#previousScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.documentElement.classList.add('dialog-open');
      document.body.classList.add('dialog-open');
      document.documentElement.setAttribute('scroll-lock', '');
      document.body.setAttribute('scroll-lock', '');

      this.#loadAndPlayVideos();
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
      this.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const dialog = this.dialogElement;

    if (!dialog || !dialog.open) return;

    const savedScrollY = this.#previousScrollY;

    this.#stopVideos();
    
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('touchmove', this.#handleTouchMove);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.documentElement.classList.remove('dialog-open', 'search-modal-open');
    document.body.classList.remove('dialog-open', 'search-modal-open');
    document.documentElement.removeAttribute('scroll-lock');
    document.body.removeAttribute('scroll-lock');

    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
    dialog.classList.remove('dialog-closing');

    window.scrollTo({ top: savedScrollY, left: 0, behavior: 'instant' });
    requestAnimationFrame(() => {
      window.scrollTo({ top: savedScrollY, left: 0, behavior: 'instant' });
    });

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Prevents body touch scrolling on backdrop or non-scrollable dialog areas.
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    const dialog = this.dialogElement;
    if (!dialog || !dialog.open) return;

    let target = /** @type {HTMLElement | null} */ (event.target);
    while (target && target !== dialog && target !== document.body) {
      const overflowY = window.getComputedStyle(target).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && target.scrollHeight > target.clientHeight) {
        return;
      }
      target = target.parentElement;
    }

    event.preventDefault();
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    const dialog = this.dialogElement;
    if (dialog?.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick = (event) => {
    const dialog = this.dialogElement;

    if (dialog && isClickedOutside(event, dialog)) {
      event.preventDefault();
      event.stopPropagation();
      this.closeDialog();
    }
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
